<template>
    <v-container fluid>
        <FocusLock>
            <v-row id="focusLockDialogForm">
                <v-col cols="12">
                    <v-card class="mb-5">
                        <v-row justify="left">
                            <v-col cols="12" sm="6" class="pa-5">
                                <!-- 1. Upload field -->
                                <v-file-upload
                                    v-model="selectedFile"
                                    accept="*"
                                    :show-size="true"
                                    clearable
                                    :browse-text="$t('lieferanten.dokument.upload.durchsuchen')"
                                    :divider-text="$t('lieferanten.dokument.upload.divider')"
                                    icon="mdi-upload"
                                    :title="$t('lieferanten.dokument.upload.draganddrop')"
                                    :counter="true"
                                    :disabled="false"
                                    :loading="false"
                                    :multiple="false"
                                    class="mb-4"
                                    style="max-width: 90%"
                                    height="320px"
                                >
                                </v-file-upload>

                                <div class="text-caption mb-4">
                                    <i>{{ $t('upload.remarks') }}</i> <br />
                                    <i>{{ allowedFileTypes.join(', ') }}</i>
                                </div>

                                <h3 class="mb-2">{{ $t('lieferanten.dokument.title') }}</h3>
                                <Field v-slot="{ errors, field }" name="selectedTitle" rules="required|max:255">
                                    <v-text-field v-bind="field" v-model="selectedTitle" :error-messages="errors" hide-details class="mb-4"></v-text-field>
                                </Field>

                                <!-- 3. Kategorie -->
                                <h3 class="mb-2">{{ $t('lieferanten.dokument.kategorie') }}</h3>
                                <v-select
                                    v-model="selectedKategorie"
                                    :items="dokumentKategorien"
                                    hide-details
                                    class="mb-4"
                                    :menu-props="{ attach: '#focusLockDialogForm' }"
                                ></v-select>

                                <!-- 4. If needed, LieferantName -->
                                <template v-if="selectedKategorie === 'vertrag' || selectedKategorie === 'individuell'">
                                    <h3 class="mb-2">{{ $t('lieferanten.dokument.selectLieferant') }}</h3>
                                    <v-text-field
                                        v-model="selectedLieferantenNummer"
                                        :placeholder="$t('lieferanten.dokument.lieferantennummer')"
                                        type="number"
                                        counter="3"
                                        class="mb-4"
                                        :rules="[(v) => !v || (v >= 0 && v <= 999) || $t('lieferanten.dokument.lieferantennummer.error')]"
                                    ></v-text-field>
                                </template>
                            </v-col>
                            <v-col cols="12" sm="6" class="pa-5" align-self="end">
                                <!-- Display the loaded information -->
                                <div v-if="getLieferantDetails" class="mb-4">
                                    <v-card variant="outlined" class="pa-3">
                                        <div v-if="getLieferantDetails.firma" class="d-flex align-center mb-2">
                                            <v-icon start>mdi-office-building</v-icon>
                                            <span class="ml-2">{{ getLieferantDetails.firma }}</span>
                                        </div>
                                        <div v-if="getLieferantDetails.lieferantenNummer" class="d-flex align-center mb-2">
                                            <v-icon start>mdi-tag</v-icon>
                                            <span class="ml-2"
                                                >{{ $t('lieferanten.dokument.lieferantennummer') }}: {{ getLieferantDetails.lieferantenNummer }}</span
                                            >
                                        </div>
                                    </v-card>
                                </div>
                            </v-col>
                        </v-row>
                    </v-card>

                    <v-row>
                        <v-col sm="6" class="d-flex">
                            <v-btn color="primary" :disabled="!canUpload" @click="uploadDokumenteWrapper(getLieferantDetails?.teilnehmerId)">
                                <v-icon start>mdi-upload</v-icon>
                                {{ $t('lieferanten.dokument.hochladen') }}
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>
        </FocusLock>
    </v-container>
</template>

<script lang="ts">
import { Component, Watch, Vue } from 'vue-facing-decorator';
import { namespace } from 'vuex-facing-decorator';
import { VFileUpload } from 'vuetify/labs/VFileUpload';

import { NAME as NAME_UPLOAD } from '@/areas/kunde/upload/store';
import { NAME as NAME_GENERAL } from '@/store/modules/general';
import { NAME as NAME_HEADER } from '@/store/modules/headerNotification';
import { NAME as NAME_SECURITY } from '@/store/modules/security';
import { NAME as NAME_RESOURCE } from '@/store/modules/resource';
import { NAME as NAME_ADMINISTRATION } from '../store';

const uploadModul = namespace(NAME_UPLOAD);
const generalModul = namespace(NAME_GENERAL);
const headerModul = namespace(NAME_HEADER);
const securityModul = namespace(NAME_SECURITY);
const resourceModul = namespace(NAME_RESOURCE);

import { GET_LANGUAGE } from '@/store/modules/resource/getters';
import { HASACCESS } from '@/store/modules/security/getters';
import { SETACCESSDENIED } from '@/store/modules/security/mutations';
import { SUBTITLE, FOOTERTITLE } from '@/store/modules/general/mutations';
import { DOKUMENTTYPEN } from '@/areas/kunde/upload/store/getters';
import { NEW_ERRORMESSAGES, CLOSE } from '@/store/modules/headerNotification/actions';

import { NAME as NAME_VALIDATION } from '@/store/modules/validation';
const validationModul = namespace(NAME_VALIDATION);
const administrationModul = namespace(NAME_ADMINISTRATION);
import { GET_ALLOWED_FILETYPES, GET_VALIDATION_KEY } from '@/store/modules/validation/getters';
import { Form, Field } from 'vee-validate';
import UploadPanel from '@/components/UploadPanel.vue';
import { LieferantDokumentUploadDto } from '@/types/LieferantDokumentUploadDto';
import { GET_LIEFERANT_DETAILS } from '@/areas/administration/store/getters';
import { CLEAR_LIEFERANT_DETAILS, LOAD_LIEFERANT_DETAILS, UPLOAD_DOKUMENT } from '@/areas/administration/store/actions';
import { i18n } from '@/plugins/i18n';

@Component({
    components: {
        UploadPanel,
        VFileUpload,
        Field,
        Form
    }
})
export default class LieferantenDokumentUpload extends Vue {
    @generalModul.Mutation(SUBTITLE) setSubTitle!: Function;
    @generalModul.Mutation(FOOTERTITLE) setFooterTitle!: Function;

    @administrationModul.Action(UPLOAD_DOKUMENT) uploadDokument!: Function;
    @administrationModul.Action(LOAD_LIEFERANT_DETAILS) loadLieferantDetails!: Function;
    @administrationModul.Action(CLEAR_LIEFERANT_DETAILS) clearLieferantDetails!: Function;
    @administrationModul.Getter(GET_LIEFERANT_DETAILS) getLieferantDetails!: Function;
    @headerModul.Action(NEW_ERRORMESSAGES) alertMessage!: Function;
    @headerModul.Action(CLOSE) closeHeadermessage!: Function;
    @securityModul.Getter(HASACCESS) hasAccess!: Function;
    @securityModul.Mutation(SETACCESSDENIED) setAccessDenied!: Function;
    @resourceModul.Getter(GET_LANGUAGE) language!: string;

    @validationModul.Getter(GET_VALIDATION_KEY) getValidation!: (key: string) => string;
    @validationModul.Getter(GET_ALLOWED_FILETYPES)
    allowedFileTypes!: Array<string>;

    selectedKategorie = '';
    selectedTitle = '';
    selectedFile;
    selectedLieferantenNummer = '';
    lieferantenInfo = '';

    get dokumentKategorien(): Array<object> {
        return [
            {
                title: this.$t('lieferanten.dokument.dokumenkategorie.vertrag'),
                value: 'vertrag'
            },
            {
                title: this.$t('lieferanten.dokument.dokumenkategorie.lieferanteninformation'),
                value: 'lieferanteninformation'
            },
            {
                title: this.$t('lieferanten.dokument.dokumenkategorie.public'),
                value: 'public'
            },
            {
                title: this.$t('lieferanten.dokument.dokumenkategorie.individuell'),
                value: 'individuell'
            }
        ];
    }

    created() {
        this.setSubTitle(this.$t('lieferanten.dokument.screen.title'));
        this.setFooterTitle('S930');
        this.clearLieferantDetails();
    }

    mounted() {
        if (!this.hasAccess('Navigation.LieferantDokumentUpload')) {
            this.setAccessDenied(true);
            this.alertMessage({ text: this.$t('errormessage.noaccesspage') });
        } else {
            this.setAccessDenied(false);
        }
    }

    get canUpload() {
        if (this.selectedFile == null) {
            return false;
        }

        if (!this.selectedTitle) {
            return false;
        }

        if (!this.selectedKategorie) {
            return false;
        }

        if ((this.selectedKategorie === 'vertrag' || this.selectedKategorie === 'individuell') && !this.getLieferantDetails) {
            return false;
        }
        return true;
    }

    chooseFiles() {
        document.getElementById('fileUpload')?.click();
    }

    uploadDokumenteWrapper(teilnehmerId: string | null) {
        this.closeHeadermessage();
        console.log('Upload started');
        const temp = new LieferantDokumentUploadDto(this.selectedFile);
        temp.titel = this.selectedTitle;
        temp.dokumentKategorie = this.selectedKategorie;
        if (temp.dokumentKategorie === 'vertrag' || temp.dokumentKategorie === 'individuell') {
            if (teilnehmerId != null) {
                temp.teilnehmerId = teilnehmerId;
            } else {
                this.alertMessage({
                    errorMessages: i18n.global.t('lieferant.upload.errormessage.teilnehmerId.pruefen')
                });
            }
        }

        this.uploadDokument(temp);
    }
    @Watch('selectedLieferantenNummer')
    onLieferantenNummerChange(val: string) {
        if (val && val.length === 3 && /^\d{3}$/.test(val)) {
            this.loadLieferantDetails(val);
        } else {
            this.clearLieferantDetails();
        }
    }
    @Watch('selectedKategorie')
    onSelectedKategorieChange(kategorie: string) {
        if (kategorie !== 'vertrag' && kategorie !== 'individuell') {
            this.clearLieferantDetails();
            this.selectedLieferantenNummer = '';
        }
    }
}
</script>

<style scoped>
/* Additional styles can be added here if needed */
</style>
